import React, { useCallback, useState, useRef, useEffect } from 'react'
import SearchInput from './SearchInput'
import { api, addCsrfHeader, type ApiResponse } from 'components/api'
import { supplierSearchPath } from 'components/routes/api'
import { debounce } from 'lodash-es'

export interface SupplierSearchResult {
  id: string
  name?: string
  location?: string
  slug?: string
  states?: string[]
  national?: boolean
  page?: string
}

export interface SearchResultsComponentProps {
  searchResults: SupplierSearchResult[] | null
  supplierName: string
  activeIndex: number
  onMouseMove: () => void
}

export type KeyboardSelection =
  | { type: 'create', supplierName: string }
  | { type: 'select', result: SupplierSearchResult }

interface SharedSupplierSearchProps {
  SearchResultsComponent: React.ComponentType<SearchResultsComponentProps>
  placeholder?: string
  required?: boolean
  onEscape?: (() => void) | null
  onKeyboardSelect?: ((selection: KeyboardSelection) => void) | null
}

const supplierSearchByName = async (name: string): Promise<ApiResponse<SupplierSearchResult[]>> => {
  const request = {
    url: supplierSearchPath(),
    headers: addCsrfHeader({}, document),
    params: { query: name }
  }
  return await api.get<unknown, SupplierSearchResult[]>(request)
}

const SharedSupplierSearch: React.FC<SharedSupplierSearchProps> = ({
  SearchResultsComponent,
  placeholder,
  required = false,
  onEscape = null,
  onKeyboardSelect = null
}) => {
  const [currentValue, setCurrentValue] = useState('')
  const [apiData, setApiData] = useState<SupplierSearchResult[] | null>(null)
  const [activeIndex, setActiveIndex] = useState(-1)
  const [isOpen, setIsOpen] = useState(false)

  const debouncedQueryRef = useRef(
    debounce(async (name: string) => {
      const apiResponse = await supplierSearchByName(name)
      setApiData(apiResponse?.data ?? [])
      setActiveIndex(-1)
      setIsOpen(true)
    }, 300)
  )

  const debouncedQuery = useCallback(
    (name: string): void => { void debouncedQueryRef.current(name) },
    []
  )

  useEffect(() => () => debouncedQueryRef.current.cancel(), [])

  useEffect(() => {
    if (activeIndex >= 0) {
      const activeElement = document.querySelector('.search-result.active')
      activeElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    }
  }, [activeIndex])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setCurrentValue(e.target.value)
    setApiData(null)
    setActiveIndex(-1)
    debouncedQuery(e.target.value)
  }

  const getResultCount = (): number => {
    const hasCreateOption = currentValue.length >= 3 ? 1 : 0
    const searchResultCount = apiData?.length ?? 0
    return hasCreateOption + searchResultCount
  }

  const getActiveDescendant = (): string | null => {
    if (activeIndex < 0 || !isOpen) return null
    const hasCreateOption = currentValue.length >= 3
    if (hasCreateOption && activeIndex === 0) {
      return 'create-supplier-option'
    }
    const resultIndex = hasCreateOption ? activeIndex - 1 : activeIndex
    const id = apiData?.[resultIndex]?.id
    return id != null ? `supplier-search-${id}` : null
  }

  const triggerKeyboardSelect = (): void => {
    const hasCreateOption = currentValue.length >= 3
    if (hasCreateOption && activeIndex === 0) {
      if (onKeyboardSelect != null) {
        onKeyboardSelect({ type: 'create', supplierName: currentValue })
      }
    } else {
      const resultIndex = hasCreateOption ? activeIndex - 1 : activeIndex
      const result = apiData?.[resultIndex]
      if (result != null && onKeyboardSelect != null) {
        onKeyboardSelect({ type: 'select', result })
      }
    }
    setIsOpen(false)
    setActiveIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    const resultCount = getResultCount()

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (activeIndex < resultCount - 1) {
          setActiveIndex(activeIndex + 1)
        }
        break
      case 'ArrowUp':
        e.preventDefault()
        if (activeIndex > -1) {
          setActiveIndex(activeIndex - 1)
        }
        break
      case 'Enter':
        if (activeIndex >= 0 && isOpen) {
          e.preventDefault()
          triggerKeyboardSelect()
        }
        break
      case 'Tab':
        setIsOpen(false)
        setActiveIndex(-1)
        break
      case 'Home':
        e.preventDefault()
        if (resultCount > 0) setActiveIndex(0)
        break
      case 'End':
        e.preventDefault()
        if (resultCount > 0) setActiveIndex(resultCount - 1)
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setActiveIndex(-1)
        if (onEscape != null) onEscape()
        break
      default:
        break
    }
  }

  const handleMouseMove = (): void => {
    if (activeIndex >= 0) setActiveIndex(-1)
  }

  const isExpanded = isOpen && currentValue.length > 0

  const renderSearchResultsIfValue = (apiData: SupplierSearchResult[] | null, currentValue: string): React.ReactNode =>
    currentValue.length > 0 && isOpen
      ? (
        <SearchResultsComponent
          searchResults={apiData}
          supplierName={currentValue}
          activeIndex={activeIndex}
          onMouseMove={handleMouseMove}
        />
        )
      : null

  return (
    <>
      <SearchInput
        currentValue={currentValue}
        handleChange={handleChange}
        handleKeyDown={handleKeyDown}
        placeholder={placeholder ?? ''}
        required={required}
        isExpanded={isExpanded}
        activeDescendant={getActiveDescendant()}
        listboxId='supplier-search-listbox'
      />
      {renderSearchResultsIfValue(apiData, currentValue)}
    </>
  )
}

export default SharedSupplierSearch
